import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateTicket } from '@/hooks/useTickets';
import { calculatePriority, FORM_ASSIGNMENTS } from '../../../../../shared/form-config';
import { useTranslation } from 'react-i18next';
import { ReporterInfoFields } from '@/components/tickets/ReporterInfoFields';
import { TicketFormButtons } from '@/components/tickets/TicketFormButtons';

const hardwareSaleSchema = z.object({
  summary: z.string().min(5, 'El resumen debe tener al menos 5 caracteres'),
  fullName: z.string().min(1, 'El nombre completo es requerido'),
  phone: z.string().min(1, 'El teléfono es requerido'),
  email: z.string().email('Email inválido'),
  // Datos de facturación (política de venta): tipo + número de documento y, para
  // Argentina, tipo de contribuyente. Valores alineados con computer-sales (backend).
  arDocumentType: z.enum(['CUIT', 'CUIL', 'DNI'], {
    required_error: 'Seleccioná el tipo de documento',
  }),
  documentNumber: z.string().min(1, 'El número de documento es requerido'),
  arTaxpayerType: z
    .enum(['Consumidor final', 'monotributo', 'responsable inscripto', 'responsable no inscripto'])
    .optional(),
  address: z.string().min(1, 'La dirección es requerida'),
  postalCode: z.string().min(1, 'El código postal es requerido'),
  city: z.string().min(1, 'La ciudad es requerida'),
  country: z.string().min(1, 'El país es requerido'),
});

type HardwareSaleFormData = z.infer<typeof hardwareSaleSchema>;

export function HardwareSaleForm() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<HardwareSaleFormData>({
    resolver: zodResolver(hardwareSaleSchema),
  });

  // ✨ Using tRPC mutation
  const createTicketMutation = useCreateTicket();
  const onSubmit = async (data: HardwareSaleFormData) => {
    const priority = calculatePriority('hardware-sale', data);
    const ticketData = {
      type: 'hardware-sale',
      title: `Venta de Hardware: ${data.summary}`,
      formData: {
        reporterName: user?.name,
        reporterEmail: user?.email,
        ...data,
      },
      attachments: [],
      priority,
      assignedTo: FORM_ASSIGNMENTS['hardware-sale'],
    };
    createTicketMutation.mutate(ticketData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('forms.hardwareSale')}</CardTitle>
        <CardDescription>
          {t('tickets.hardwaresaleform.gestionar_venta_de_equipos_usados')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="summary">
              {t('forms.description')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="summary"
              placeholder={t('tickets.hardwaresaleform.ej_macbook_pro_2020_16gb_ram')}
              {...register('summary')}
            />
            {errors.summary && (
              <p className="text-sm text-destructive">{errors.summary.message}</p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fullName">
                {t('forms.fullName')} <span className="text-destructive">*</span>
              </Label>
              <Input id="fullName" {...register('fullName')} />
              {errors.fullName && (
                <p className="text-sm text-destructive">{errors.fullName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                {t('forms.phone')} <span className="text-destructive">*</span>
              </Label>
              <Input id="phone" type="tel" {...register('phone')} />
              {errors.phone && (
                <p className="text-sm text-destructive">{errors.phone.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="email">
                {t('forms.email')} <span className="text-destructive">*</span>
              </Label>
              <Input id="email" type="email" {...register('email')} />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="arDocumentType">
                {t('forms.documentType')} <span className="text-destructive">*</span>
              </Label>
              <Select onValueChange={(value) => setValue('arDocumentType', value as any)}>
                <SelectTrigger id="arDocumentType">
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DNI">DNI</SelectItem>
                  <SelectItem value="CUIT">CUIT</SelectItem>
                  <SelectItem value="CUIL">CUIL</SelectItem>
                </SelectContent>
              </Select>
              {errors.arDocumentType && (
                <p className="text-sm text-destructive">{errors.arDocumentType.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="documentNumber">
                {t('forms.documentNumber')} <span className="text-destructive">*</span>
              </Label>
              <Input id="documentNumber" {...register('documentNumber')} />
              {errors.documentNumber && (
                <p className="text-sm text-destructive">{errors.documentNumber.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="arTaxpayerType">{t('forms.taxpayerType')}</Label>
              <Select onValueChange={(value) => setValue('arTaxpayerType', value as any)}>
                <SelectTrigger id="arTaxpayerType">
                  <SelectValue placeholder="Seleccionar…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Consumidor final">{t('tickets.hardwaresaleform.consumidor_final')}</SelectItem>
                  <SelectItem value="monotributo">Monotributo</SelectItem>
                  <SelectItem value="responsable inscripto">{t('tickets.hardwaresaleform.responsable_inscripto')}</SelectItem>
                  <SelectItem value="responsable no inscripto">{t('tickets.hardwaresaleform.responsable_no_inscripto')}</SelectItem>
                </SelectContent>
              </Select>
              {errors.arTaxpayerType && (
                <p className="text-sm text-destructive">{errors.arTaxpayerType.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">
              {t('forms.address')} <span className="text-destructive">*</span>
            </Label>
            <Input id="address" {...register('address')} />
            {errors.address && (
              <p className="text-sm text-destructive">{errors.address.message}</p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="postalCode">
                {t('forms.postalCode')} <span className="text-destructive">*</span>
              </Label>
              <Input id="postalCode" {...register('postalCode')} />
              {errors.postalCode && (
                <p className="text-sm text-destructive">{errors.postalCode.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">
                {t('forms.city')} <span className="text-destructive">*</span>
              </Label>
              <Input id="city" {...register('city')} />
              {errors.city && (
                <p className="text-sm text-destructive">{errors.city.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">
                {t('forms.country')} <span className="text-destructive">*</span>
              </Label>
              <Input id="country" {...register('country')} />
              {errors.country && (
                <p className="text-sm text-destructive">{errors.country.message}</p>
              )}
            </div>
          </div>

          <ReporterInfoFields name={user?.name} email={user?.email} />

          <TicketFormButtons isPending={createTicketMutation.isPending} />
        </form>
      </CardContent>
    </Card>
  );
}
